import { FC, useEffect, useRef, useState } from 'react';
import {
    Area,
    AreaChart as ReAreaChart,
    CartesianGrid,
    Customized,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { ProfessionAreaChartItem } from 'src/models/pages/profession';
import AreaChartHeader from 'src/pages/Profession/components/SectionStatistics/AreaChart/AreaChartHeader';

import CustomTooltipPoint from 'src/pages/Profession/components/SectionStatistics/AreaChart/Chart/CustomTooltipPoint';
import {
    CHART_HEIGHT,
    ANIMATION_DURATION,
    CHART_MARGIN,
    ERROR_COLOR,
    ERROR_GRADIENT_ID,
    STROKE_COLOR,
    SUCCESS_COLOR,
    SUCCESS_GRADIENT_ID,
} from 'src/pages/Profession/components/SectionStatistics/AreaChart/Chart/constants';
import {
    getAreaBgColor,
    getChartItemColor,
    numberTickFormatter,
} from 'src/pages/Profession/components/SectionStatistics/AreaChart/Chart/helpers';
import setLastItem from 'src/pages/Profession/components/SectionStatistics/AreaChart/Chart/setLastItem';

import styles from './styles.less';

interface Props {
    data: ProfessionAreaChartItem[];
    initValue: ProfessionAreaChartItem;
    onClick: () => void;
}

const Chart: FC<Props> = ({ data, initValue, onClick }) => {
    const [selectItem, setSelectItem] = useState<ProfessionAreaChartItem>(initValue);
    const color = getChartItemColor(selectItem);
    const areaBackground = getAreaBgColor(selectItem);

    /* eslint-disable  @typescript-eslint/no-explicit-any */
    const chartRef = useRef<any>();

    const handleSelectItem = (data: ProfessionAreaChartItem) => {
        setSelectItem(data);
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (chartRef.current) {
                setLastItem(chartRef.current);
            }
        }, ANIMATION_DURATION);

        return () => clearTimeout(timeout);
    }, []);

    return (
        <div className={styles.chartContainer}>
            <AreaChartHeader selectedItem={selectItem} />
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <ReAreaChart data={data} margin={CHART_MARGIN} onClick={onClick} ref={chartRef}>
                    <Customized component={<CustomTooltipPoint />} />
                    <XAxis axisLine={false} tickLine={false} dataKey="year" />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={numberTickFormatter}
                        width={70}
                        domain={[0, 'auto']}
                    />
                    <Tooltip
                        trigger="click"
                        cursor={{ stroke: color, strokeWidth: 1 }}
                        content={({ active, payload }) => {
                            if (!!active && !!payload?.length) {
                                handleSelectItem(payload[0].payload as ProfessionAreaChartItem);
                            }
                            return null;
                        }}
                    />
                    <CartesianGrid vertical={false} strokeWidth={1} stroke={STROKE_COLOR} width={1000} x={0} />
                    <Area
                        dot={{ fill: color, strokeWidth: 0, r: 6.67 / 2 }}
                        activeDot={{ fill: color, strokeWidth: 0, r: 10 / 2 }}
                        type="monotone"
                        dataKey="value"
                        stroke={color}
                        strokeWidth={2}
                        fillOpacity={1}
                        fill={areaBackground}
                        animationDuration={ANIMATION_DURATION}
                        animationEasing="ease-out"
                    />
                    <defs>
                        <linearGradient id={SUCCESS_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={SUCCESS_COLOR} stopOpacity={1} />
                            <stop offset="100%" stopColor={SUCCESS_COLOR} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id={ERROR_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={ERROR_COLOR} stopOpacity={1} />
                            <stop offset="100%" stopColor={ERROR_COLOR} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                </ReAreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default Chart;
